import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CaretRight, Clock, CalendarBlank, Shield } from '@phosphor-icons/react'
import { userApi } from '@/api/users'
import { orgApi } from '@/api/organizations'
import { UserProfileEdit } from '@/components/users/UserProfileEdit'
import { UserDangerZone } from '@/components/users/UserDangerZone'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays < 1) return 'today'
  if (diffDays === 1) return '1 day ago'
  if (diffDays < 7) return `${diffDays} days ago`
  const diffWeeks = Math.floor(diffDays / 7)
  if (diffWeeks === 1) return '1 week ago'
  if (diffWeeks < 5) return `${diffWeeks} weeks ago`
  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths === 1) return '1 month ago'
  if (diffMonths < 12) return `${diffMonths} months ago`
  const diffYears = Math.floor(diffDays / 365)
  if (diffYears === 1) return '1 year ago'
  return `${diffYears} years ago`
}

export function UserDetail() {
  const { orgId, userId } = useParams()

  const {
    data: user,
    isLoading: userLoading,
    error: userError,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ['user', orgId, userId],
    queryFn: () => userApi.detail(orgId!, userId!),
    enabled: !!orgId && !!userId,
  })

  const { data: org } = useQuery({
    queryKey: ['organizations', 'detail', orgId],
    queryFn: () => orgApi.detail(orgId!),
    enabled: !!orgId,
  })

  // Set document title
  useEffect(() => {
    if (user) {
      document.title = `${user.name} - Copilot Core Admin`
    }
    return () => {
      document.title = 'Copilot Core Admin'
    }
  }, [user])

  // Loading skeleton
  if (userLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-4 w-80" />
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-6 w-16 rounded-full" />
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  // Error state
  if (userError) {
    return (
      <div className="space-y-6">
        <nav className="flex items-center gap-1 text-sm text-muted-foreground">
          <Link to="/organizations" className="hover:text-foreground transition-colors">
            Organizations
          </Link>
          <CaretRight size={14} />
          <Link to={`/organizations/${orgId}`} className="hover:text-foreground transition-colors">
            {org?.name ?? 'Organization'}
          </Link>
        </nav>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-lg font-medium text-destructive mb-2">
              Failed to load user
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {userError.message}
            </p>
            <Button variant="outline" onClick={() => refetchUser()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Not found
  if (!user) {
    return (
      <div className="space-y-6">
        <nav className="flex items-center gap-1 text-sm text-muted-foreground">
          <Link to="/organizations" className="hover:text-foreground transition-colors">
            Organizations
          </Link>
          <CaretRight size={14} />
          <Link to={`/organizations/${orgId}`} className="hover:text-foreground transition-colors">
            {org?.name ?? 'Organization'}
          </Link>
        </nav>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-lg font-medium mb-2">User not found</p>
            <p className="text-sm text-muted-foreground mb-4">
              The user you are looking for does not exist.
            </p>
            <Link to={`/organizations/${orgId}?tab=users`}>
              <Button variant="outline">Back to Users</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link to="/organizations" className="hover:text-foreground transition-colors">
          Organizations
        </Link>
        <CaretRight size={14} />
        <Link
          to={`/organizations/${orgId}`}
          className="hover:text-foreground transition-colors"
        >
          {org?.name ?? 'Organization'}
        </Link>
        <CaretRight size={14} />
        <Link
          to={`/organizations/${orgId}?tab=users`}
          className="hover:text-foreground transition-colors"
        >
          Users
        </Link>
        <CaretRight size={14} />
        <span className="text-foreground font-medium">{user.name}</span>
      </nav>

      {/* Header row */}
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight">{user.name}</h1>
        <Badge variant={user.is_active ? 'default' : 'destructive'}>
          {user.is_active ? 'Active' : 'Inactive'}
        </Badge>
        {user.role_display_name && (
          <Badge variant="secondary">{user.role_display_name}</Badge>
        )}
      </div>

      {/* Profile card (editable) */}
      <UserProfileEdit user={user} orgId={orgId!} onUpdate={() => refetchUser()} />

      {/* Activity summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 rounded-lg border p-4">
              <CalendarBlank className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Account Created</p>
                <p className="mt-1 text-sm font-medium">
                  {timeAgo(user.created_at)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(user.created_at).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-lg border p-4">
              <Clock className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Last Login</p>
                <p className="mt-1 text-sm font-medium">
                  {user.last_login ? timeAgo(user.last_login) : 'Never'}
                </p>
                {user.last_login && (
                  <p className="text-xs text-muted-foreground">
                    {new Date(user.last_login).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-lg border p-4">
              <Shield className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
                <p className="mt-1 text-sm font-medium">
                  {user.is_active ? 'Active' : 'Inactive'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {user.role_display_name ?? 'No role assigned'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <UserDangerZone user={user} orgId={orgId!} onAction={() => refetchUser()} />
    </div>
  )
}
